import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, authHeaders, authStorageKey } from '../api/client';
import type { components } from '../api/schema';

export type CurrentUser = components['schemas']['UserResponse'];
export type LoginForm = components['schemas']['LoginRequest'];

interface AuthContextValue {
  token: string;
  currentUser: CurrentUser | null;
  isAuthLoading: boolean;
  isBootstrapping: boolean;
  authError: string;
  signIn: (form: LoginForm) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredToken(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  window.localStorage.removeItem(authStorageKey);
  return window.sessionStorage.getItem(authStorageKey) || '';
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [token, setToken] = useState<string>(() => readStoredToken());
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string>('');
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => Boolean(readStoredToken()));

  const persistToken = useCallback((nextToken: string) => {
    if (nextToken) {
      window.sessionStorage.setItem(authStorageKey, nextToken);
    } else {
      window.sessionStorage.removeItem(authStorageKey);
    }
    window.localStorage.removeItem(authStorageKey);
    setToken(nextToken);
  }, []);

  const signOut = useCallback(() => {
    persistToken('');
    setCurrentUser(null);
    setAuthError('');
  }, [persistToken]);

  const signIn = useCallback(
    async (form: LoginForm) => {
      setIsAuthLoading(true);
      setAuthError('');
      try {
        const response = await api.post('/api/auth/login', form);
        const access = response.data?.access_token as string | undefined;
        const user = response.data?.user as CurrentUser | undefined;
        if (!access) {
          throw new Error('Login response missing access_token');
        }
        persistToken(access);
        if (user) {
          setCurrentUser(user);
        } else {
          const me = await api.get('/api/auth/me', authHeaders(access));
          setCurrentUser(me.data as CurrentUser);
        }
      } catch (error) {
        const detail =
          (error as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ||
          (error as Error).message ||
          'Sign-in failed';
        setAuthError(detail);
        throw error;
      } finally {
        setIsAuthLoading(false);
      }
    },
    [persistToken],
  );

  // Hydrate currentUser on mount when we already have a token in storage.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setIsBootstrapping(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const me = await api.get('/api/auth/me', authHeaders(token));
        if (!cancelled) {
          setCurrentUser(me.data as CurrentUser);
        }
      } catch {
        if (!cancelled) {
          persistToken('');
          setCurrentUser(null);
        }
      } finally {
        if (!cancelled) {
          setIsBootstrapping(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ token, currentUser, isAuthLoading, isBootstrapping, authError, signIn, signOut }),
    [token, currentUser, isAuthLoading, isBootstrapping, authError, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
